import React, {Suspense} from "react";
import {Navigate, Route} from "react-router-dom";
import Loading from "./pages/Loading";
import SectionLoading from "./components/Project/SectionLoading";
import SectionNotFound from "./components/Project/SectionNotFound";
import {SECTIONS, TAB_ELEMENTS} from "./components/Project/sections";
import {DEFAULT_SECTION, tabsFor} from "./components/Project/sectionPaths";

// The whole project shell, and everything under it, is its own chunk - a
// visitor who never opens a project never pays for it. Individual sections
// and tabs split further still (see components/Project/sections.js).
const Project = React.lazy(() => import("./pages/Project"));
const DesignPage = React.lazy(() => import("./components/Design/DesignPage"));
const ScopePhases = React.lazy(() => import("./components/Scope/ScopePhases"));
const ScopeClass = React.lazy(() => import("./components/Scope/ScopeClass"));
const NewScopeClassTab = React.lazy(() => import("./components/Scope/NewScopeClassTab"));

/**
 * A section's children: its tabs, if it has a fixed set of them (see
 * sectionPaths.js's SECTION_TABS), otherwise just the section itself.
 *
 * A tabbed section's own element is the tab bar, and the tab is what renders
 * beneath it - which is why the tab, not the section, is the leaf here. Its
 * index has no element: TabbedSection redirects to the first tab the viewer
 * can actually see, which for Settings depends on their permissions.
 *
 * Nothing here mentions records. An open task, bug or support ticket used to be
 * a path segment, which meant declaring three of them under every section - and
 * then, once tabs arrived, under every tab. It's a query parameter now (see
 * Linked/useOpenRecord.js), orthogonal to the path, so the route table doesn't
 * have to know records exist at all.
 */
const sectionChildren = (slug, element) => {
    const config = tabsFor(slug);
    if (!config) return <Route index element={element}/>;

    return config.tabs.map((tab) => (
        <Route path={tab.slug} element={TAB_ELEMENTS[slug][tab.slug]} key={tab.slug}/>
    ));
};

/**
 * The project's routes, as one nested block under a single layout.
 *
 * This replaced nine flat routes that all rendered <Project/>, which then
 * re-dispatched on a `:route` param through a switch to decide what to show.
 * The switch is gone: the route table maps a URL to a section directly, from
 * the same list the sidebar is built from (see components/Project/sections.js),
 * and <Project/> is now purely the layout - chrome, project-wide data, and an
 * <Outlet/> where the section goes.
 *
 * Returned as an element rather than exported as a constant so it can be
 * spread into <Routes> in AppRouter.js and turned into route objects by the
 * route test (see AppRouter.routes.test.js) from this same definition.
 */
export const projectRoutes = () => (
    <Route path="/project/:id" element={<Suspense fallback={<Loading/>}><Project/></Suspense>} key={"project"}>
        {/* A project on its own isn't a view - send it to the section a
            project opens on. `replace`, so Back doesn't bounce off it. */}
        <Route index element={<Navigate to={DEFAULT_SECTION} replace/>}/>

        {
            // Scope is handled on its own below - its tabs are data, not a
            // fixed set, so it can't be generated from the same shape.
            SECTIONS.filter(({slug}) => slug !== "scope").map(({slug, element}) => (
                <Route path={slug} key={slug} element={tabsFor(slug) ? element : undefined}>
                    {sectionChildren(slug, element)}
                </Route>
            ))
        }

        {/* Scope's tabs are one per scope class, made and unmade by the user,
            so they can't come from the fixed table the sections above use - see
            components/Scope/Scope.js. The `class/` prefix keeps a user-named
            class from colliding with the other two tabs or with an overlay
            segment. */}
        <Route path={"scope"} element={SECTIONS.find((s) => s.slug === "scope").element} key={"scope"}>
            <Route path={"phases"} element={<Suspense fallback={<SectionLoading/>}><ScopePhases/></Suspense>} key={"phases"}/>
            <Route path={"class/:typeSlug"} element={<Suspense fallback={<SectionLoading/>}><ScopeClass/></Suspense>} key={"class"}/>
            <Route path={"new"} element={<Suspense fallback={<SectionLoading/>}><NewScopeClassTab/></Suspense>} key={"new-class"}/>
        </Route>

        {/* One design page rather than the table of contents. Nested under the
            section's own slug, so it keeps the same URL it always had. */}
        <Route
            path={"design/:typeSlug/:scopeSlug"}
            element={<Suspense fallback={<SectionLoading/>}><DesignPage/></Suspense>}
            key={"design-page"}
        />

        {/* Kanban is the one section that names a sprint in its path - its
            board only ever shows one sprint, and which one belongs in the
            address. The segment is the sprint's number within the project (1
            for its first, 2 for its second), not a database id. */}
        <Route
            path={"sprint/:sprintIndex/kanban"}
            element={SECTIONS.find((s) => s.slug === "kanban").element}
            key={"kanban-sprint"}
        />

        {/* A section that doesn't exist says so, rather than rendering the
            project's chrome around an empty content area - which is what a
            mistyped URL used to do, since the old switch returned null for
            anything it didn't recognise. */}
        <Route path={"*"} element={<SectionNotFound/>} key={"section-not-found"}/>
    </Route>
);
